import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./api-client";
// ============================================
// Services Hooks
// ============================================
export function useServices() {
    return useQuery({
        queryKey: ["services"],
        queryFn: async () => {
            const response = await apiClient.get("/v1/services");
            if (!response.ok)
                throw new Error(response.error.message);
            return response.data;
        },
    });
}
export function useFeaturedServices() {
    return useQuery({
        queryKey: ["services", "featured"],
        queryFn: async () => {
            const response = await apiClient.get("/v1/services/featured");
            if (!response.ok)
                throw new Error(response.error.message);
            return response.data;
        },
    });
}
export function useService(slug) {
    return useQuery({
        queryKey: ["services", slug],
        queryFn: async () => {
            const response = await apiClient.get(`/v1/services/${slug}`);
            if (!response.ok)
                throw new Error(response.error.message);
            return response.data;
        },
        enabled: !!slug,
    });
}
// ============================================
// Projects Hooks
// ============================================
export function useProjects() {
    return useQuery({
        queryKey: ["projects"],
        queryFn: async () => {
            const response = await apiClient.get("/v1/projects");
            if (!response.ok)
                throw new Error(response.error.message);
            return response.data;
        },
    });
}
export function useFeaturedProjects() {
    return useQuery({
        queryKey: ["projects", "featured"],
        queryFn: async () => {
            const response = await apiClient.get("/v1/projects/featured");
            if (!response.ok)
                throw new Error(response.error.message);
            return response.data;
        },
    });
}
export function useProject(slug) {
    return useQuery({
        queryKey: ["projects", slug],
        queryFn: async () => {
            const response = await apiClient.get(`/v1/projects/${slug}`);
            if (!response.ok)
                throw new Error(response.error.message);
            return response.data;
        },
        enabled: !!slug,
    });
}
// ============================================
// Testimonials Hooks
// ============================================
export function useTestimonials() {
    return useQuery({
        queryKey: ["testimonials"],
        queryFn: async () => {
            const response = await apiClient.get("/v1/testimonials");
            if (!response.ok)
                throw new Error(response.error.message);
            return response.data;
        },
    });
}
export function useFeaturedTestimonials() {
    return useQuery({
        queryKey: ["testimonials", "featured"],
        queryFn: async () => {
            const response = await apiClient.get("/v1/testimonials/featured");
            if (!response.ok)
                throw new Error(response.error.message);
            return response.data;
        },
    });
}
// ============================================
// Blog Hooks
// ============================================
export function useBlogPosts() {
    return useQuery({
        queryKey: ["blog"],
        queryFn: async () => {
            const response = await apiClient.get("/v1/blog");
            if (!response.ok)
                throw new Error(response.error.message);
            return response.data;
        },
    });
}
export function useBlogPost(slug) {
    return useQuery({
        queryKey: ["blog", slug],
        queryFn: async () => {
            const response = await apiClient.get(`/v1/blog/${slug}`);
            if (!response.ok)
                throw new Error(response.error.message);
            return response.data;
        },
        enabled: !!slug,
    });
}
// ============================================
// Pages Hooks
// ============================================
export function usePage(slug) {
    return useQuery({
        queryKey: ["pages", slug],
        queryFn: async () => {
            const response = await apiClient.get(`/v1/pages/${slug}`);
            if (!response.ok)
                throw new Error(response.error.message);
            return response.data;
        },
        enabled: !!slug,
    });
}
export function useSubmitContactForm() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            const response = await apiClient.post("/v1/contact-requests", data);
            if (!response.ok)
                throw new Error(response.error.message);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate and refetch any contact-related queries
            queryClient.invalidateQueries({ queryKey: ["contact-requests"] });
        },
    });
}
export function useSubmitLeadForm() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            const response = await apiClient.post("/v1/leads", data);
            if (!response.ok)
                throw new Error(response.error.message);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["leads"] });
        },
    });
}
export function useTrackEvent() {
    return useMutation({
        mutationFn: async (data) => {
            const response = await apiClient.post("/v1/analytics/event", data);
            if (!response.ok)
                throw new Error(response.error.message);
            return response.data;
        },
    });
}
